import { Button, CssBaseline, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress } from '@mui/material';
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { useCommStore } from './common/axios';
import { ArticleListPage } from './feature/article/ArticleListPage';
import { CustomerListPage } from './feature/customer/CustomerListPage';
import { CustomerPage } from './feature/customer/CustomerPage';
import DashboardPage from './feature/DashboardPage';
import { InventoryTransactionListPage } from './feature/inventoryTransaction/InventoryTransactionListPage';
import { SystemTaskListPage } from './feature/systemTask/SystemTaskListPage';
import LoginPage from './feature/LoginPage';
import { CountryDefaultsPage } from './feature/product/CountryDefaultsPage';
import { ProductDefaultsPage } from './feature/product/ProductDefaultsPage';
import { ProductListPage } from './feature/product/ProductListPage';
import { PaymentErrorReportPage } from './feature/reports/PaymentErrorReportPage';
import { RejectedImportItemListPage } from './feature/reports/RejectedImportItemListPage';
import { RetentionReportPage } from './feature/reports/RetentionReportPage';
import { InventoryTransactionsReportPage } from './feature/reports/InventoryTransactionsReportPage';
import { DunningRunPage } from './feature/runs/DunningRunPage';
import { OrderImportRunPage } from './feature/runs/OrderImportRunPage';
import { MailOrderRunPage } from './feature/runs/MailOrderRunPage';
import { ManualWriteOffRunPage } from './feature/runs/ManualWriteOffRunPage';
import { PaymentRunPage } from './feature/runs/PaymentRunPage';
import { ReturnRunPage } from './feature/runs/ReturnRunPage';
import { ShipmentRunPage } from './feature/runs/ShipmentRunPage';
import { StarterRunPage } from './feature/runs/StarterRunPage';
import { WriteOffRunPage } from './feature/runs/WriteOffRunPage';
import { CollectionAgencyListPage } from "./feature/settings/CollectionAgencyListPage";
import { CustomerNoteActionTypeListPage } from "./feature/settings/CustomerNoteActionTypeListPage";
import { CustomerNoteTypeListPage } from "./feature/settings/CustomerNoteTypeListPage";
import { CustomerStateListPage } from "./feature/settings/CustomerStateListPage";
import { DefaultValuesPage } from "./feature/settings/DefaultValuesPage";
import { InvoiceStateListPage } from "./feature/settings/InvoiceStateListPage";
import { OfferMailOrderShipmentListPage } from "./feature/settings/OfferMailOrderShipmentListPage";
import { OrderStateListPage } from "./feature/settings/OrderStateListPage";
import { PaymentMethodListPage } from "./feature/settings/PaymentMethodListPage";
import { DeliveryMethodListPage } from "./feature/settings/DeliveryMethodListPage";
import { PrintTemplateListPage } from "./feature/settings/PrintTemplateListPage";
import { TransactionCodeListPage } from './feature/settings/TransactionCodeListPage';
import { WarehousesListPage } from "./feature/settings/WarehousesListPage";
import { SpecialActionListPage } from "./feature/settings/SpecialActionListPage";
import { UserListPage } from './feature/user/UserListPage';
import AnonymousLayout from './layout/AnonymousLayout';
import AuthLayout from './layout/AuthLayout';
import { RunHistoryPage } from './feature/runs/RunHistoryPage';
import { TmRewardsReportPage } from './feature/reports/TmRewardsReportPage';
import { useConfig } from './common/config';
import { OrderCycleSkipImportPage } from './feature/runs/OrderCycleSkipImportPage';
import { CustomerNoteImportPage } from './feature/runs/CustomerNoteImportPage';
import { SalesJournalPage } from './feature/reports/SalesJournalPage';
import { SalesJournalSettingsPage } from './feature/settings/SalesJournalSettingsPage';
import { PaymentJournalSettingsPage } from './feature/settings/PaymentJournalSettingsPage';
import { PaymentJournalPage } from './feature/reports/PaymentJournalPage';
import { CustomerReactivationRunPage } from './feature/runs/CustomerReactivationRunPage';
import { OverpaymentsReportPage } from './feature/reports/OverpaymentsReportPage';
import { MailOrderCopyRunPage } from './feature/runs/MailOrderCopyRunPage';
import { OrderDelayRunPage } from './feature/runs/OrderDelayRunPage';
import { ReplacementArticleListPage } from './feature/settings/ReplacementArticleListPage';
import { MailOrderArticleReplaceRunPage } from './feature/runs/MailOrderArticleReplaceRunPage';
import { ContOrderDeactivationRunPage } from './feature/runs/ContOrderDeactivationRunPage';
import { GdprRunPage } from './feature/runs/GdprRunPage';
import { VatUpdateRunPage } from './feature/runs/VatUpdateRunPage';
import { CustomerStopCodeListPage } from './feature/settings/CustomerStopCodeListPage';
import { CallCenterListPage } from './feature/settings/CallCenterListPage';
import { TmOrderPage } from './feature/tm/TmOrderPage';
import { TmOrderListPage } from './feature/tm/TmOrderListPage';
import { TmCallResultListPage } from './feature/settings/TmCallResultListPage';
import { TmOrdersReportPage } from './feature/reports/TmOrdersReportPage';
import { TmOperatorsReportPage } from './feature/reports/TmOperatorsReportPage';
import { ResourceListPage } from './feature/settings/ResourceListPage';
import { EPrefContentTypeListPage } from './feature/settings/EPrefContentTypeListPage';
import { EPrefFrequencyListPage } from './feature/settings/EPrefFrequencyListPage';
import { EPrefTypesListPage } from './feature/settings/EPrefTypesListPage';
import { EPreferencesRunPage } from './feature/runs/EPreferencesRunPage';
import { OnlineReturnBatchRunPage } from './feature/runs/OnlineReturnBatchRunPage';
import { AllowedCharacterListPage } from './feature/settings/AllowedCharacterListPage';

const statusDict: { [num: number]: string; } = {
    400: "Invalid data",
    403: "Forbidden",
    404: "Not found",
    409: "Conflict",
    500: "Server error",
}

function CommStore() {
    const { isCommunicatingForLongTime, errors } = useCommStore(p => ({ isCommunicatingForLongTime: p.isCommunicatingForLongTime, errors: p.errors }));
    return <>
        {isCommunicatingForLongTime && <LinearProgress style={{ position: "absolute", width: "100%", zIndex: 10000 }} />}
        {errors.map(p => <Dialog key={p.id} open onClose={() => useCommStore.getState().clearError(p)}>
            <DialogTitle style={{ margin: 10 }}>Error{p.status == null ? "" : ` (${statusDict[p.status] == null ? p.status : statusDict[p.status]})`}</DialogTitle>
            <DialogContent style={{ margin: 10, minWidth: 300, whiteSpace: "pre-line" }}>
                {typeof p.message === "string" ? p.message : ""}
            </DialogContent>
            <DialogActions style={{ margin: 10 }}>
                <Button onClick={() => useCommStore.getState().clearError(p)}>Ok</Button>
            </DialogActions>
        </Dialog>)}
    </>;
}

const router = createBrowserRouter(createRoutesFromElements(<Route path="/">
    <Route element={<AnonymousLayout />}>
        <Route path="/login" element={<LoginPage />} />
    </Route>
    <Route element={<AuthLayout />}>
        <Route path="/" element={<DashboardPage />} />

        <Route path="/customers" element={<CustomerListPage />} />
        <Route path="/customers/:custId" element={<CustomerPage />} />

        <Route path="/products" element={<ProductListPage />} />

        <Route path="/articles" element={<ArticleListPage />} />

        <Route path="/inventory-transactions" element={<InventoryTransactionListPage />} />
        <Route path="/system-tasks" element={<SystemTaskListPage />} />

        <Route path="/users" element={<UserListPage />} />

        <Route path="/runs/shipment" element={<ShipmentRunPage />} />
        <Route path="/runs/dunning" element={<DunningRunPage />} />
        <Route path="/runs/write-off" element={<WriteOffRunPage />} />
        <Route path="/runs/mo" element={<MailOrderRunPage />} />
        <Route path="/runs/starter" element={<StarterRunPage />} />
        <Route path="/runs/batch-payments" element={<PaymentRunPage />} />
        <Route path="/runs/batch-returns" element={<ReturnRunPage />} />
        <Route path="/runs/import" element={<Navigate to="/runs/order-import" />} />
        <Route path="/runs/order-import" element={<OrderImportRunPage />} />
        <Route path="/runs/manual-write-off" element={<ManualWriteOffRunPage />} />
        <Route path="/runs/order-cycle-skip-import" element={<OrderCycleSkipImportPage />} />
        <Route path="/runs/customer-note-import" element={<CustomerNoteImportPage />} />
        <Route path="/runs/customer-reactivation" element={<CustomerReactivationRunPage />} />
        <Route path="/runs/history" element={<RunHistoryPage />} />
        <Route path="/runs/mo-copy" element={<MailOrderCopyRunPage />} />
        <Route path="/runs/order-delay" element={<OrderDelayRunPage />} />
        <Route path="/runs/mo-article-replace" element={<MailOrderArticleReplaceRunPage />} />
        <Route path="/runs/email-preferences" element={<EPreferencesRunPage />} />
        <Route path="/runs/order-deactivation" element={<ContOrderDeactivationRunPage />} />
        <Route path="/runs/gdpr" element={<GdprRunPage />} />
        <Route path="/runs/vat-update" element={<VatUpdateRunPage />} />
        <Route path="/runs/online-return-batch" element={<OnlineReturnBatchRunPage />} />

        <Route path="/runs/shipment/fake" element={<ShipmentRunPage fake />} />
        <Route path="/runs/dunning/fake" element={<DunningRunPage fake />} />
        <Route path="/runs/write-off/fake" element={<WriteOffRunPage fake />} />
        <Route path="/runs/mo/fake" element={<MailOrderRunPage fake />} />
        <Route path="/runs/starter/fake" element={<StarterRunPage fake />} />

        <Route path="/reports/rejected-duplicates" element={<RejectedImportItemListPage />} />
        <Route path="/reports/retention" element={<RetentionReportPage />} />
        <Route path="/reports/payment-errors" element={<PaymentErrorReportPage />} />
        <Route path="/reports/transaction" element={<InventoryTransactionsReportPage />} />
        <Route path="/reports/tm-rewards" element={<TmRewardsReportPage />} />
        <Route path="/reports/sales-journal" element={<SalesJournalPage />} />
        <Route path="/reports/payment-journal" element={<PaymentJournalPage />} />
        <Route path="/reports/overpayments" element={<OverpaymentsReportPage />} />
        <Route path="/reports/tm-orders" element={<TmOrdersReportPage />} />
        <Route path="/reports/tm-operators" element={<TmOperatorsReportPage />} />

        <Route path="/settings/transaction-codes" element={<TransactionCodeListPage />} />
        <Route path="/settings/warehouses" element={<WarehousesListPage />} />
        <Route path="/settings/payment-methods" element={<PaymentMethodListPage />} />
        <Route path="/settings/delivery-methods" element={<DeliveryMethodListPage />} />
        <Route path="/settings/invoice-states" element={<InvoiceStateListPage />} />
        <Route path="/settings/customer-states" element={<CustomerStateListPage />} />
        <Route path="/settings/order-states" element={<OrderStateListPage />} />
        <Route path="/settings/customer-stop-codes" element={<CustomerStopCodeListPage />} />
        <Route path="/settings/customer-note-types" element={<CustomerNoteTypeListPage />} />
        <Route path="/settings/customer-note-action-types" element={<CustomerNoteActionTypeListPage />} />
        <Route path="/settings/collection-agencies" element={<CollectionAgencyListPage />} />
        <Route path="/settings/print-templates" element={<PrintTemplateListPage />} />
        <Route path="/settings/default-values" element={<DefaultValuesPage />} />
        <Route path="/settings/default-values/country" element={<CountryDefaultsPage />} />
        <Route path="/settings/default-values/products" element={<ProductDefaultsPage />} />
        <Route path="/settings/mail-order-templates" element={<OfferMailOrderShipmentListPage />} />
        <Route path="/settings/special-actions" element={<SpecialActionListPage />} />
        <Route path="/settings/replacement-articles" element={<ReplacementArticleListPage />} />
        <Route path="/settings/tm-call-results" element={<TmCallResultListPage />} />

        <Route path="/settings/email-preference-content" element={<EPrefContentTypeListPage />} />
        <Route path="/settings/email-preference-frequencies" element={<EPrefFrequencyListPage />} />
        <Route path="/settings/email-preference-types" element={<EPrefTypesListPage />} />

        <Route path="/settings/sales-journal" element={<SalesJournalSettingsPage />} />
        <Route path="/settings/payment-journal" element={<PaymentJournalSettingsPage />} />

        <Route path="/settings/call-centers" element={<CallCenterListPage />} />

        <Route path="/settings/resources" element={<ResourceListPage />} />
        <Route path="/settings/allowed-characters" element={<AllowedCharacterListPage />} />
        
        <Route path="/tm/orders/:id" element={<TmOrderPage />} />
        <Route path="/tm/orders" element={<TmOrderListPage />} />
    </Route>
</Route>));

export default function App() {
    const { theme, landCode } = useConfig(p => p.clientData);
    return <>
        {landCode && theme && <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CssBaseline />
                <CommStore />
                <RouterProvider router={router} />
            </LocalizationProvider>
        </ThemeProvider>}
    </>;
}
